import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { ErrorHandlingModal } from '@components/ErrorHandlingModal';
import { FilesSelector } from '@components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from '@components/FilesSelector/hooks/useReadFilesAsBase64';
import { ControlledTextField } from '@components/Form';
import { ModalDialog } from '@components/ModalDialog';
import { RequestStatus } from '@features/request/requestSlice';
import type { CatalogUerOtherCostTemplateInput } from '@gql/graphql';
import { useMethodsConfirmationDialog } from '@hooks/useMethodsConfirmationDialog';
import { cnConsta, Grid, GridItem, Loader } from '@uikit';
import { destroyAgGridTableFilters } from '@utils';

import { FieldLabel } from '@consta/uikit/FieldLabel';
import type { GridApi } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useCatalogOtherCostTemplateDataForUpdate } from '../hooks/useCatalogOtherCostTemplateDataForUpdate';
import { useCreateOtherCostTemplate } from '../hooks/useCreateOtherCostTemplate';
import { useEditOtherCostTemplateItem } from '../hooks/useEditOtherCostTemplateItem';

import { useIsUniqueNameOtherCostTemplate } from './hooks/useIsUniqueNameOtherCostTemplate';

export const COMPONENT_NAME = 'TextField' as const;
export const cnTextField = cnConsta(COMPONENT_NAME);

export interface IDataEditModal {
    name: string;
    id: number;
    content: File;
}

export const AddOrEditOtherCostTemplatesModal = ({
    isModalOpened,
    closeHandler,
    isCreating = false,
    openedTemplateId,
    refreshData,
    handlerOtherCostTemplatesUpdatedDate,
    gridRefApi,
    setFilterValues,
}: {
    isModalOpened: boolean;
    closeHandler: (state: boolean) => void;
    isCreating?: boolean;
    openedTemplateId?: number | null;
    refreshData: () => void;
    handlerOtherCostTemplatesUpdatedDate: () => void;
    gridRefApi: GridApi | undefined;
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
}) => {
    const dispatch = useDispatch();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [isFileStateDirty, setIsFileStateDirty] = useState<boolean>(false);

    const methods = useForm<any>({
        criteriaMode: 'all',
    });
    const { control, reset, handleSubmit, setError, clearErrors, formState } = methods;

    const onSuccessHandler = useCallback(() => {
        refreshData();
        handlerOtherCostTemplatesUpdatedDate();
        destroyAgGridTableFilters({
            api: gridRefApi,
            columns: ['content', 'name'],
        });
        setFilterValues({});
    }, [gridRefApi, handlerOtherCostTemplatesUpdatedDate, refreshData, setFilterValues]);

    const {
        resetEditData,
        otherCostTemplateEditData,
        otherCostTemplateEditStatus,
        otherCostTemplateEditError,
        otherCostTemplateEditRequest,
        isSavingInProgress,
    } = useEditOtherCostTemplateItem({
        onSuccessHandler,
    });

    const watchName = useWatch({
        control,
        name: 'name',
    });

    const { isUniqueName, isUniqueNameFetching } = useIsUniqueNameOtherCostTemplate({
        name: watchName,
    });

    useEffect(() => {
        if (
            formState.isDirty &&
            !isUniqueNameFetching &&
            watchName &&
            watchName !== otherCostTemplateEditData?.data?.name &&
            !isUniqueName
        ) {
            setError('name', {
                message: 'Шаблон прочих затрат с таким наименованием уже существует',
            });
        } else {
            clearErrors('name');
        }
    }, [
        clearErrors,
        otherCostTemplateEditData?.data?.name,
        isUniqueName,
        setError,
        watchName,
        formState.isDirty,
        isUniqueNameFetching,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => closeHandler?.(false),
        });

    const makeRequestHandler = useCallback(
        (filesToProcess) => {
            setFiles(filesToProcess);
            setIsFileStateDirty(true);
        },
        [setFiles],
    );

    const { otherForUpdateData, otherForUpdateStatus, otherCostTemplatesReset } =
        useCatalogOtherCostTemplateDataForUpdate({ setFiles, id: openedTemplateId });

    const isDataFetching = useMemo(() => otherForUpdateStatus === RequestStatus.FETCHING, [otherForUpdateStatus]);

    const {
        createOtherCostTemplateStatus,
        createOtherCostTemplateError,
        createOtherCostTemplateRequest,
        isCreatingInProgress,
        createOtherCostTemplateData,
        resetCreatingData,
    } = useCreateOtherCostTemplate({
        onSuccessHandler,
    });

    const handleCloseModal = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            resetCreatingData(null);
            resetEditData();
            otherCostTemplatesReset(undefined);
            closeHandler?.(false);
        }
    }, [
        closeHandler,
        formState?.isDirty,
        handleOpenConfirmationDialog,
        otherCostTemplatesReset,
        resetCreatingData,
        resetEditData,
    ]);

    const createOrEditStatus = useMemo(
        () => (isCreating ? createOtherCostTemplateStatus : otherCostTemplateEditStatus),
        [createOtherCostTemplateStatus, isCreating, otherCostTemplateEditStatus],
    );

    const createOrEditError = useMemo(
        () => (isCreating ? createOtherCostTemplateError : otherCostTemplateEditError),
        [createOtherCostTemplateError, isCreating, otherCostTemplateEditError],
    );

    const isCommit = useMemo(
        () => isCreatingInProgress || isSavingInProgress,
        [isCreatingInProgress, isSavingInProgress],
    );

    const createOrEditData = useMemo(
        () => (isCreating ? createOtherCostTemplateData : otherCostTemplateEditData),
        [createOtherCostTemplateData, isCreating, otherCostTemplateEditData],
    );

    const dataEditModal = useMemo<IDataEditModal>(
        () => ({
            name: formState.defaultValues?.name || '',
            content: (formState.defaultValues?.content as any) || '',
            id: otherForUpdateData?.id || 0,
        }),
        [formState.defaultValues?.content, formState.defaultValues?.name, otherForUpdateData?.id],
    );

    const base64Files = useReadFilesAsBase64(files);

    const onSubmit = useCallback(
        (data) => {
            const variables: CatalogUerOtherCostTemplateInput = {
                name: data?.name,
                file: {
                    dataUrl: base64Files?.[0].dataUrl || '',
                    name: files[0].name,
                },
            };

            if (isCreating) {
                dispatch(
                    createOtherCostTemplateRequest({
                        input: variables,
                    }),
                );
            } else {
                dispatch(
                    otherCostTemplateEditRequest({
                        id: dataEditModal?.id as number,
                        input: variables,
                    }),
                );
            }

            setIsSubmitted(true);
        },
        [
            base64Files,
            files,
            isCreating,
            dispatch,
            createOtherCostTemplateRequest,
            otherCostTemplateEditRequest,
            dataEditModal?.id,
        ],
    );

    useEffect(() => {
        if (isSubmitted && createOrEditData?.result) {
            resetCreatingData(null);
            resetEditData();
            otherCostTemplatesReset(undefined);
            closeHandler?.(false);
        }
    }, [
        closeHandler,
        createOrEditData?.result,
        isSubmitted,
        otherCostTemplatesReset,
        resetCreatingData,
        resetEditData,
    ]);

    const title = useMemo(
        () => (isCreating ? 'Создание шаблона прочих затрат' : 'Редактирование шаблона прочих затрат'),
        [isCreating],
    );
    const btnConfirmLabel = useMemo(() => (isCreating ? 'Создать' : 'Сохранить'), [isCreating]);

    const onCloseSaveErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    useEffect(() => {
        if (!isCreating) {
            reset({
                name: otherForUpdateData?.name,
                file: otherForUpdateData?.file,
            });
        }
    }, [isCreating, reset, otherForUpdateData?.name, otherForUpdateData?.file]);

    const btnConfirmDisabled = !formState.isValid || !(formState.isDirty || isFileStateDirty) || !files.length;

    return (
        <>
            {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}

            <ModalDialog
                isOpen={isModalOpened}
                title={title}
                onClose={handleCloseModal}
                onConfirm={handleSubmit(onSubmit)}
                withTitleCloseButton
                style={{ maxWidth: '520px', width: '100%' }}
                isCommit={isCommit}
                isOnClickOutsideDisable
                btnConfirmLabel={btnConfirmLabel}
                btnConfirmDisabled={btnConfirmDisabled}
            >
                <FormProvider {...methods}>
                    <Grid rowGap="s" cols="1" style={{ maxWidth: '90%', padding: '24px' }}>
                        <GridItem>
                            <ControlledTextField
                                name="name"
                                label="Наименование"
                                control={control}
                                type="text"
                                directionTooltip="rightCenter"
                                tooltipSize="m"
                                labelPosition="top"
                                rules={{
                                    required: 'Обязательное поле',
                                    maxLength: {
                                        value: 255,
                                        message: 'Максимальное количество символов – 255',
                                    },
                                }}
                                required
                            />
                        </GridItem>
                        <GridItem>
                            <div style={{ display: 'flex', marginBottom: '4px' }}>
                                <FieldLabel size="s" className={cnTextField('Label', ['TextField-Label'])} required>
                                    Загрузка шаблона прочих затрат
                                </FieldLabel>
                            </div>
                            <FilesSelector
                                accept={['.xlsx']}
                                withoutEndingOfWordFile
                                fullHeight={!files.length}
                                onFilesChange={makeRequestHandler}
                                isExternalFilesState
                                files={files}
                                withDownloadAction
                                locale={{
                                    'file-invalid-type': ({ file }) =>
                                        `Формат загружаемого файла ${file.name} не соответствует требованиям. Для загрузки используйте файл формата xlsx`,
                                }}
                            />
                        </GridItem>
                    </Grid>
                </FormProvider>
                <ErrorHandlingModal
                    titleText={title}
                    customButtonText="Понятно"
                    viewButton="primary"
                    isSubmitted={isSubmitted}
                    statusRequest={createOrEditStatus}
                    dataRequest={createOrEditData}
                    errorsRequest={createOrEditError}
                    handleCloseModalError={onCloseSaveErrorModal}
                    style={{ maxWidth: '520px' }}
                />
                <ConfirmationDialog
                    textHeader="Отмена действия"
                    textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                    cancelButtonText="Не отменять"
                    confirmButtonText="Отменить"
                    isOpened={isOpenConfirmationDialog}
                    onSubmit={handleActionQuery}
                    onClose={handleCloseConfirmationDialog}
                />
            </ModalDialog>
        </>
    );
};

AddOrEditOtherCostTemplatesModal.displayName = 'AddOrEditOtherCostTemplatesModal';
